import React from 'react';
import cx from 'classnames';

import {TestId} from '../../../utils/testIds';
import {Icon, IconTypes} from '../../elements/Icon/Icon';

import type {IconSource} from '../../../../design-tokens/generated/Icons';
import styles from './CollapsableContainer.module.scss';
import {TextHeading} from '../../elements/TextHeading/TextHeading';
import {FontVariant} from '../../../../design-tokens/constants';
import {Button} from '../../elements/Button/Button';
import {BadgeCounter} from '../../elements/BadgeCounter/BadgeCounter';
import {BoxFooter} from '../../elements/BoxFooter/BoxFooter';

export const CollapsableContainer: React.FC<
    React.PropsWithChildren<{
        isOpen: boolean;
        badgeCount?: number;
        openStyling?: string;
        headerEndContent?: React.ReactElement;
        footerContent?: React.ReactElement;
        collapseToggleAriaLabel?: string;
        icon: IconSource;
        title: string;
        testId?: string;
        onToggle: (currentOpenState: boolean) => void;
        scrollElementRef?: React.RefObject<HTMLDivElement>;
    }>
> = ({
    isOpen,
    badgeCount = 0,
    collapseToggleAriaLabel = 'Toggle collapsable container',
    openStyling,
    children,
    onToggle,
    headerEndContent,
    footerContent,
    icon,
    title,
    testId,
    scrollElementRef,
}) => (
    <div
        data-testid={testId}
        className={cx(styles.collapsableContainer, isOpen && openStyling)}
    >
        <div
            className={cx(styles.header, {
                [styles.displayingHeaderEndContent]:
                    isOpen && !!headerEndContent,
                [styles.isOpen]: isOpen,
            })}
        >
            <div className={styles.headerStartContent}>
                <Button
                    onClick={() => onToggle(isOpen)}
                    data-testid={TestId.ButtonCollapsableContainerToggle}
                    size="small"
                    modifier="square"
                    variant="transparent"
                    noEnhancerPadding
                    aria-label={collapseToggleAriaLabel}
                    enhancerStart={
                        <Icon
                            source={
                                isOpen
                                    ? IconTypes.IconChevronLeft
                                    : IconTypes.IconChevronRight
                            }
                            size="small"
                        />
                    }
                />
                <div
                    className={cx(styles.iconContainer, {
                        [styles.isOpen]: isOpen,
                    })}
                >
                    <Icon source={icon} size={'small'} />
                    {!isOpen && (
                        <BadgeCounter
                            className={styles.badgeCounterContainer}
                            badgeClassName={styles.badgeCounter}
                            variant="info"
                            isVisible={badgeCount > 0}
                            number={badgeCount}
                            shouldAnimate={false}
                        />
                    )}
                </div>
                {isOpen && (
                    <TextHeading
                        htmlTag="h2"
                        fontVariant={FontVariant.BodyBold}
                    >
                        <span>{title}</span>{' '}
                        {badgeCount > 0 && <span>({badgeCount})</span>}
                    </TextHeading>
                )}
            </div>
            {isOpen && headerEndContent && (
                <div className={styles.headerEndContent}>
                    {headerEndContent}
                </div>
            )}
        </div>
        {isOpen && (
            <div ref={scrollElementRef} className={styles.content}>
                {children}
            </div>
        )}
        {isOpen && footerContent && (
            <BoxFooter hasFooterShadow padding="small">
                {footerContent}
            </BoxFooter>
        )}
    </div>
);
